import { observable, configure, action, computed, runInAction } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class YearlySummaryStore extends LocalModel {
    constructor() {
        super('yearlySummary', {
            namePlural: 'yearlySummaries',
            sort: '-year',
            limit: 10,
            api: {
                load: {
                    url: '/api/yearlysummary/',
                    params: {},
                },
            },
        });
    }

    @observable summary = null;

    @observable loading = false;

    @observable error = null;

    @observable currentCardIndex = 0;

    @observable seasonActive = false;

    @observable year = null;

    @observable appType = null;

    /**
     * Check if summary season is active (Dec-Jan)
     */
    isSummarySeasonActive() {
        const month = new Date().getMonth();
        return month === 11 || month === 0; // Dec or Jan
    }

    /**
     * Get the year to show summary for
     */
    getSummaryYear() {
        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();
        return month === 0 ? year - 1 : year;
    }

    /**
     * Load user's yearly summary
     */
    @action
    async loadSummary(year = null, appType = null, regenerate = false) {
        runInAction(() => {
            this.loading = true;
            this.error = null;
        });

        const targetYear = year || this.getSummaryYear();
        let url = `/api/yearlysummary/${targetYear}`;
        const params = [];
        if (appType) {
            params.push(`appType=${appType}`);
        }
        if (regenerate) {
            params.push('regenerate=1');
        }
        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }

        try {
            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response && response.status === 200) {
                    this.summary = response.data;
                    this.seasonActive = response.meta?.seasonActive ?? true;
                    this.year = response.meta?.year || targetYear;
                    this.appType = response.meta?.appType || 'shooting';
                } else if (response && response.status === 403) {
                    this.seasonActive = false;
                    this.error = response.message;
                } else {
                    this.error = response?.message || 'Failed to load summary';
                }
                this.loading = false;
            });
        } catch (err) {
            runInAction(() => {
                this.error = err.message || 'Failed to load summary';
                this.loading = false;
            });
        }

        return this.summary;
    }

    /**
     * Load public summary by UUID (for sharing)
     */
    @action
    async loadPublicSummary(uuidv4) {
        runInAction(() => {
            this.loading = true;
            this.error = null;
        });

        try {
            const response = await util.fetchApi(`/api/yearlysummary/public/${uuidv4}`);

            runInAction(() => {
                if (response && response.status === 200) {
                    this.summary = response.data;
                    this.year = response.meta?.year;
                    this.appType = response.meta?.appType;
                } else {
                    this.error = response?.message || 'Summary not found';
                }
                this.loading = false;
            });
        } catch (err) {
            runInAction(() => {
                this.error = err.message || 'Failed to load summary';
                this.loading = false;
            });
        }

        return this.summary;
    }

    /**
     * Regenerate summary with fresh data
     */
    @action
    async regenerateSummary(year = null, appType = null) {
        runInAction(() => {
            this.loading = true;
            this.error = null;
        });

        const targetYear = year || this.getSummaryYear();
        let url = `/api/yearlysummary/${targetYear}/regenerate`;
        if (appType) {
            url += `?appType=${appType}`;
        }

        try {
            const response = await util.fetchApi(url, { method: 'POST' });

            runInAction(() => {
                if (response && response.status === 200) {
                    this.summary = response.data;
                    this.year = response.meta?.year || targetYear;
                    this.appType = response.meta?.appType;
                } else {
                    this.error = response?.message || 'Failed to regenerate summary';
                }
                this.loading = false;
            });
        } catch (err) {
            runInAction(() => {
                this.error = err.message || 'Failed to regenerate summary';
                this.loading = false;
            });
        }

        return this.summary;
    }

    /**
     * Generate share image for a specific stat card
     */
    @action
    async generateShareImage(cardKey) {
        if (!this.summary?.id) {
            return null;
        }

        try {
            const response = await util.fetchApi(
                `/api/yearlysummary/${this.summary.id}/screenshot/${cardKey}`,
                { method: 'POST' }
            );

            if (response && response.status === 200 && response.data) {
                runInAction(() => {
                    // Update the summary with new share images
                    if (response.data.shareImages) {
                        this.summary = {
                            ...this.summary,
                            shareImages: response.data.shareImages,
                        };
                    }
                });
                return response.data.currentShareImage;
            }
            return null;
        } catch (err) {
            console.error('Error generating share image:', err);
            return null;
        }
    }

    /**
     * Get share image for a specific card
     */
    getShareImage(cardKey) {
        if (!this.summary?.shareImages) {
            return null;
        }
        const image = this.summary.shareImages.find(s => s.statKey === cardKey);
        return image?.image || null;
    }

    /**
     * Set current card index (for scroll animation tracking)
     */
    @action
    setCurrentCardIndex(index) {
        this.currentCardIndex = index;
    }

    /**
     * Reset store state
     */
    @action
    reset() {
        this.summary = null;
        this.loading = false;
        this.error = null;
        this.currentCardIndex = 0;
        this.seasonActive = false;
        this.year = null;
        this.appType = null;
    }

    /**
     * Refresh page - reload summary with current settings
     */
    @action
    async refreshPage() {
        await this.loadSummary(this.year, this.appType, false);
    }

    /**
     * Get share URL for current summary
     */
    @computed
    get shareUrl() {
        if (!this.summary?.uuidv4) {
            return null;
        }
        const baseUrl = window.location.origin;
        return `${baseUrl}/#/public/yearlysummary/${this.summary.uuidv4}`;
    }

    /**
     * Get share URL for a specific card
     */
    getShareUrlForCard(cardKey) {
        if (!this.summary?.uuidv4) {
            return null;
        }
        const baseUrl = window.location.origin;
        if (cardKey && cardKey !== 'full-summary') {
            return `${baseUrl}/#/public/yearlysummary/${this.summary.uuidv4}/${cardKey}`;
        }
        return `${baseUrl}/#/public/yearlysummary/${this.summary.uuidv4}`;
    }

    /**
     * Check if summary has data
     */
    @computed
    get hasSummary() {
        return !!this.summary?.id;
    }

    /**
     * Get stats based on app type
     */
    @computed
    get stats() {
        if (!this.summary) {
            return null;
        }
        if (this.appType === 'mushing') {
            return this.summary.mushing;
        } else if (this.appType === 'dyrejournal') {
            return this.summary.dyrejournal;
        }
        return this.summary.shooting;
    }
}

const store = new YearlySummaryStore();
export default store;
